import { useEffect, useState } from 'react';
import { CapacitorCookies } from '@capacitor/core';
import { Link } from 'react-router-dom';
import {
  deleteAccount,
  fetchNotificationPreferences,
  saveNotificationPreferences,
  type NotificationPreferences,
} from '../lib/api';
import { signOut } from '../lib/auth';
import { isNativeShell, resolveApiUrl, NATIVE_API_ORIGIN } from '../lib/api-origin';
import { unregisterPushNotifications } from '../lib/push';
import { checkPushPermission, enablePushOnDevice, type PushPermissionState } from '../lib/push';

/**
 * Robust sign-out for both web and native shells.
 *
 * The previous implementation just awaited `signOut()` and reloaded.
 * On the Capacitor WebView, if better-auth's call rejected for any
 * native-specific reason (CapacitorHttp quirk, network blip, etc.)
 * the user landed on the SPA reload with a still-valid cookie and a
 * still-valid server session — i.e. signed back in immediately.
 *
 * Belt + braces: try the SDK signOut; on failure, POST the sign-out
 * endpoint directly through the patched fetch; then nuke every cookie
 * the WebView is willing to surface to JS so the next request can't
 * carry a dead session token back in. Finally `location.replace` so
 * the SPA boots fresh with no history entry that would let the user
 * walk back into the authed shell.
 */
async function performSignOut() {
  // 1. Server-side: best-effort drop the device token + push registration
  //    so a shared phone doesn't keep getting this account's pings.
  try {
    await Promise.allSettled([
      unregisterPushNotifications(),
      fetch(resolveApiUrl('/api/devices'), { method: 'DELETE', credentials: 'include' }),
    ]);
  } catch {
    // ignore
  }

  // 2. Server-side: drop the session. Try the SDK first because it
  //    knows the right endpoint + payload; fall back to a raw POST if
  //    that fails.
  let sdkOk = false;
  try {
    await signOut();
    sdkOk = true;
  } catch {
    sdkOk = false;
  }
  if (!sdkOk) {
    try {
      await fetch(resolveApiUrl('/api/auth/sign-out'), {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // give up on the server clear; the client clear below will at
      // least free the UI.
    }
  }

  // 3. Client-side: nuke every cookie we can reach.
  //
  //    The better-auth session cookie lives in the NATIVE cookie jar
  //    because it's httpOnly + SameSite=None+Secure for cross-origin
  //    delivery. document.cookie can't see or delete it. On the web
  //    SPA the document.cookie clear is enough because the cookie is
  //    same-origin and the next page load has no way to read it
  //    either; on the Capacitor shell we MUST go through the native
  //    CapacitorCookies API which talks to the OS cookie store the
  //    same one CapacitorHttp uses for cross-origin requests.
  //
  //    Without this step on native, the SDK signOut returns success,
  //    location.replace('/') reloads the SPA, useSession() runs, hits
  //    /api/auth/session with the still-present session cookie, the
  //    server happily returns the (now-recreated-by-better-auth-on-
  //    GET, depending on plugin behaviour) session, and the user
  //    lands back on the matches page — exactly the bug Luis hit.
  try {
    document.cookie.split(';').forEach((c) => {
      const eq = c.indexOf('=');
      const name = (eq > -1 ? c.substring(0, eq) : c).trim();
      if (!name) return;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
  } catch {
    // ignore
  }
  if (isNativeShell()) {
    try {
      // Wipe everything in the native cookie jar — easiest belt for
      // the httpOnly session cookie we can't reach from JS. The
      // CapacitorCookies plugin must be enabled in capacitor.config.ts
      // for this to do anything; otherwise it no-ops silently and
      // sign-out continues to fail.
      await CapacitorCookies.clearAllCookies();
    } catch {
      // ignore — fall through to per-cookie deletion below.
    }
    try {
      // Belt-and-braces: target the better-auth session cookie name
      // explicitly on lessalone.app. Better-Auth defaults to
      // `better-auth.session_token` and also writes a
      // `better-auth.csrf_token` companion. Set them with Max-Age=0
      // on the API origin so the next request can't ship them.
      const expired = 'Max-Age=0; Path=/; Secure; SameSite=None';
      const setCookie = (
        CapacitorCookies as unknown as {
          setCookie?: (opts: {
            url: string;
            key: string;
            value: string;
            path?: string;
            expires?: string;
          }) => Promise<void>;
        }
      ).setCookie;
      if (setCookie) {
        await Promise.allSettled(
          ['better-auth.session_token', 'better-auth.csrf_token'].flatMap((name) => [
            setCookie({
              url: NATIVE_API_ORIGIN,
              key: name,
              value: '',
              path: '/',
              expires: 'Thu, 01 Jan 1970 00:00:00 GMT',
            }),
          ]),
        );
      }
    } catch {
      // ignore
    }
  }

  // 4. Local storage cleanup so any cached auth fragments
  //    (better-auth keeps no-op-but-still-present entries) don't
  //    inadvertently rehydrate state on the next mount.
  try {
    window.localStorage.removeItem('better-auth.session_token');
    window.localStorage.removeItem('better-auth.session');
    // The OTP-pending key from Landing.tsx — clear in case the user
    // signs out mid-flow on a different account.
    window.localStorage.removeItem('lessalone:otp:pending');
  } catch {
    // ignore
  }

  // 5. Hard reload so React mounts fresh + useSession re-checks. Using
  //    replace() (not assign()) so the Back gesture from the Landing
  //    page can't restore the previous authed view from history.
  window.location.replace('/');
}

type PrefRow = { key: keyof NotificationPreferences; label: string; help: string };

const EMAIL_ROWS: PrefRow[] = [
  {
    key: 'emailOnNewMatch',
    label: 'When you pair me with a new match',
    help: 'A short note the moment a new conversation appears — peer nickname, never their email.',
  },
  {
    key: 'emailOnNewMessage',
    label: 'When a match sends me a message',
    help: 'A short email tells you a reply is waiting, never what was said.',
  },
  {
    key: 'emailOn48hNudge',
    label: 'When a match has been waiting a while',
    help: 'A 48-hour nudge so an async conversation doesn’t fall through the cracks.',
  },
  {
    key: 'emailOnGraduationPeer',
    label: 'When a match steps away well',
    help: 'You hear about the ending as a win, not a ghost.',
  },
  {
    key: 'emailOnCheckinReminder',
    label: 'Check-in reminders',
    help: 'Light prompts at week 2 / week 6 to take the short UCLA-3 check-in.',
  },
];

const PUSH_ROWS: PrefRow[] = [
  {
    key: 'pushOnNewMessage',
    label: 'When a match sends me a message',
    help: 'A native ping on your phone — only fires if you installed the app.',
  },
  {
    key: 'pushOn48hNudge',
    label: 'When a match has been waiting a while',
    help: 'Same 48-hour nudge, delivered as a push instead of (or in addition to) email.',
  },
];

/**
 * Per-user notification opt-outs, split by channel. The server treats a
 * missing row as all-on, so "off" is the only state the user actively
 * records.
 */
export function NotificationSettings() {
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pushState, setPushState] = useState<PushPermissionState | null>(null);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    fetchNotificationPreferences()
      .then(setPrefs)
      .catch(() => setError('Could not load your preferences.'));
    void checkPushPermission().then(setPushState);
  }, []);

  async function handleEnablePush() {
    setEnabling(true);
    const next = await enablePushOnDevice();
    setEnabling(false);
    setPushState(next);
  }

  function toggle(key: keyof NotificationPreferences) {
    if (!prefs) return;
    setSaved(false);
    setPrefs({ ...prefs, [key]: !prefs[key] });
  }

  async function save() {
    if (!prefs) return;
    setBusy(true);
    setError(null);
    const result = await saveNotificationPreferences(prefs);
    setBusy(false);
    if (result.ok) setSaved(true);
    else setError(result.error);
  }

  function renderRows(rows: PrefRow[]) {
    if (!prefs) return null;
    return rows.map((row) => (
      <label key={row.key} className="consent-item">
        <input type="checkbox" checked={prefs[row.key]} onChange={() => toggle(row.key)} />
        <span>
          <strong>{row.label}</strong>
          <br />
          <span className="muted">{row.help}</span>
        </span>
      </label>
    ));
  }

  return (
    <main className="settings">
      <div className="card">
        <p className="step-label">
          <Link to="/">← Home</Link>
        </p>
        <h1>Notifications</h1>
        <p className="muted">
          Choose what LessAlone pings you about, and how. Your conversations and matches keep
          working either way — these toggles only affect what reaches your inbox or your phone.
        </p>
        {!prefs && !error && <p className="muted">Loading…</p>}
        {prefs && (
          <div className="stack">
            <section>
              <h2>Email</h2>
              <div className="stack">{renderRows(EMAIL_ROWS)}</div>
            </section>

            <section>
              <h2>Push</h2>
              {pushState === 'unavailable' && (
                <p className="muted">
                  Push notifications only fire inside the LessAlone mobile app. Install it on your
                  phone to receive these — these toggles still apply once it's running.
                </p>
              )}
              {pushState === 'prompt' && (
                <div className="stack">
                  <p className="muted">
                    Push isn't enabled on this device yet. Allow it and your phone will buzz only
                    for messages from your match — never anything else.
                  </p>
                  <button
                    type="button"
                    className="secondary"
                    disabled={enabling}
                    onClick={() => void handleEnablePush()}
                  >
                    {enabling ? 'Asking…' : 'Enable on this device'}
                  </button>
                </div>
              )}
              {pushState === 'denied' && (
                <p className="muted">
                  Push is blocked for LessAlone in your phone's OS settings. Re-enable it there
                  (Settings → Apps → LessAlone → Notifications), then reopen this page.
                </p>
              )}
              {pushState === 'granted' && (
                <p className="muted">
                  Push is enabled on this device. Choose what we ping you about:
                </p>
              )}
              <div className="stack">{renderRows(PUSH_ROWS)}</div>
            </section>

            <button type="button" className="settings-save" disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Save preferences'}
            </button>
            {saved && <p className="muted">Saved.</p>}
            {error && <p className="error">{error}</p>}

            <SignOutSection />

            <DataExportSection />

            <DangerZone />
          </div>
        )}
      </div>
    </main>
  );
}

/**
 * Prominent, full-width sign-out card. The previous secondary button
 * was easy to miss between the "Save preferences" row and the danger
 * zone, and Luis flagged that he could barely see it on mobile. This
 * version stakes its own card, leads with a Fraunces heading, and
 * uses a strong sage-deep button that reads as a real action — not
 * an afterthought wedged in next to the prefs.
 */
function SignOutSection() {
  const [busy, setBusy] = useState(false);
  return (
    <section className="signout-section">
      <h2>Sign out</h2>
      <p className="muted">
        Sign out of LessAlone on this device. Your conversations and matches stay on your account —
        you can sign back in any time with the same email.
      </p>
      <button
        type="button"
        className="signout-button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void performSignOut().catch(() => {
            // performSignOut already does location.replace on the happy path;
            // if it threw before that we want the button to be tappable again.
            setBusy(false);
          });
        }}
      >
        {busy ? 'Signing out…' : 'Sign out of this device'}
      </button>
    </section>
  );
}

/**
 * "Download my data" — the right-to-access companion to account deletion.
 * Fetches /api/me/export through the patched fetch (so it works in the
 * Capacitor shell against the prod origin) and saves it as a JSON file.
 */
function DataExportSection() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function downloadMyData() {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch(resolveApiUrl('/api/me/export'), { credentials: 'include' });
      if (!res.ok) {
        setErr('Could not prepare your export. Please try again.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lessalone-my-data.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setErr('Could not prepare your export. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="data-export-section">
      <h2>Your data</h2>
      <p className="muted">
        Download a copy of the data LessAlone holds for your account — your profile, your
        communities, and the messages you've sent.
      </p>
      <button
        type="button"
        className="secondary"
        disabled={busy}
        onClick={() => void downloadMyData()}
      >
        {busy ? 'Preparing…' : 'Download my data'}
      </button>
      {err && <p className="error">{err}</p>}
    </section>
  );
}

/**
 * Anonymize-in-place self-delete. The button opens a confirm panel
 * where the user must type "I understand" verbatim before the call
 * fires. Server tombstones the user row, ends active conversations,
 * tombstones every membership nickname to "[deleted]", drops
 * sessions, then signs out. Append-only safety data (audit_log,
 * moderation_event, message bodies) is intentionally preserved.
 */
function DangerZone() {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function doDelete() {
    if (typed !== 'I understand') {
      setErr('Please type "I understand" exactly to confirm.');
      return;
    }
    setBusy(true);
    setErr(null);
    const result = await deleteAccount();
    setBusy(false);
    if (result.ok) {
      // Hard navigate. The server already invalidated the session;
      // a fresh load lands on Landing and the user is signed out.
      window.location.href = '/';
      return;
    }
    setErr(result.error);
  }

  return (
    <section className="danger-zone">
      <h2>Delete my account</h2>
      <p className="muted">
        This anonymizes your profile and ends every active conversation. Your nickname is replaced
        with [deleted] for everyone you've talked to. Some records are kept (moderator notes, crisis
        events) per our safety policy — see the <a href="/privacy">privacy policy</a>.
      </p>
      {!open && (
        <button type="button" className="secondary" onClick={() => setOpen(true)}>
          Delete my account…
        </button>
      )}
      {open && (
        <div className="stack" style={{ gap: '0.6rem', marginTop: '0.8rem' }}>
          <p className="muted">
            Type <strong>I understand</strong> below to confirm. This can't be undone.
          </p>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="I understand"
            disabled={busy}
          />
          <div className="modal-actions">
            <button type="button" disabled={busy} onClick={() => void doDelete()}>
              {busy ? 'Deleting…' : 'Delete my account'}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setTyped('');
                setErr(null);
              }}
            >
              Cancel
            </button>
          </div>
          {err && <p className="error">{err}</p>}
        </div>
      )}
    </section>
  );
}
